import { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

import {
  BaseInput,
  BaseInputGroupRow,
  BaseInputRowComponent,
  BaseInputSectionHeader,
  PeriodValue,
} from '../types/inputTypes'
import { ReportRowType } from '../types/reportRowType'

interface UseReportDataProps<T> {
  data: BaseInput<T> | null
}

interface UseReportDataReturn<T> {
  allExpandableIds: string[]
  reportData: BaseInputRowComponent<T>[]
  periods: PeriodValue[]
}

export const useReportData = <T>({ data }: UseReportDataProps<T>): UseReportDataReturn<T> => {
  const processedData = useMemo(() => {
    if (!data || !data.components) {
      return { allExpandableIds: [], reportData: [], periods: data?.header || [] }
    }

    const ids: string[] = []

    /* Regular tree traversing to pick up ids from groups and add UUIDs to accounts */
    /* Accounts and groups potentially can be in different sections of the report. UUIDs help not to confuse React */
    /* Expected to run only once after fetch */

    const setUniqueIdAndAddToIds = (item: BaseInputGroupRow<T> | BaseInputSectionHeader<T>) => {
      if (!item.uniqueId) {
        item.uniqueId = `${item.label.replaceAll(' ', '-')}-${uuidv4()}`
      }
      ids.push(item.uniqueId)
    }

    const traverse = (sections: BaseInputRowComponent<T>[]) => {
      for (const section of sections) {
        if (section.type === ReportRowType.Section && section.children) {
          for (const child of section.children) {
            if (child.type === ReportRowType.Group && child.children) {
              setUniqueIdAndAddToIds(child)

              for (const accountId of Object.keys(child.children)) {
                const account = child.children[accountId]

                if (!account.uniqueId) {
                  account.uniqueId = `${account.id}-${uuidv4()}`
                }
              }
            } else if (child.type === ReportRowType.Sum) {
              setUniqueIdAndAddToIds(child)
            } else if (child.type === ReportRowType.Section && child.children) {
              traverse([child])
            }
          }
        }
      }
    }

    traverse(data.components)

    return {
      allExpandableIds: ids,
      reportData: data.components,
      periods: data.header || [],
    }
    // better for memoisation purposes in this case
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.components])

  return {
    allExpandableIds: processedData.allExpandableIds,
    reportData: processedData.reportData,
    periods: processedData.periods,
  }
}
